import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  editContactSync,
  saveContactSync,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';

import { SubmitHandler, useForm } from 'react-hook-form';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { ContactSyncModel } from '../../../../../types';
import StepOne from './step-one';
import StepTwo from './step-two';
import StepSix from './step-six';
import StepFour from './step-four';
import StepThree from './step-three';
import { flushSync } from 'react-dom';
import ContactSyncSteps from './contact-sync-steps';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import SharedScheduleStep from '../../../../shared/shared-schedule-step';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
interface ContactSyncDialogProps {
  open: boolean;
  handleClose: () => void;
  domains: string[];
  changeLogRoles: string[];
  contactSyncList: ContactSyncModel[];
}

interface CreateDialogProps extends ContactSyncDialogProps {
  dialogType: 'create';
  selectedContactSync: null;
}

interface EditDialogProps extends ContactSyncDialogProps {
  dialogType: 'edit';
  selectedContactSync: ContactSyncModel;
}

export default function ContactSyncDialog({
  open,
  handleClose,
  changeLogRoles,
  domains,
  contactSyncList,
  dialogType,
  selectedContactSync,
}: CreateDialogProps | EditDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { activeLabs } = useUserSettings();

  const { setSnackbar } = useStore();
  const [domainAutoCompleteResponse, setDomainAutoCompleteResponse] =
    useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [stepCompleted, setStepCompleted] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [activeStep, setActiveStep] = useState(0);
  let steps = ['Setup Contact Sync', 'Sources', 'Targets', 'Review'];

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting },
    trigger,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm<Partial<ContactSyncModel>>({
    defaultValues:
      dialogType === 'create'
        ? {
            name: '',
            syncType: 'contact',
            targetType: 'User',
            targetGroupName: '',
            addToMyContacts: false,
            scheduled: false,
            description: '',
            respectDirectoryContactSharing: false,
            sourceUser: '',
            sources: [],
            groupTitle: '',
            groupHref: '',
            targets: [],
            runImmediately: false,
            trackSrcCreate: false,
            trackSrcDelete: false,
            trackSrcUpdate: false,
            trackDestCreate: false,
            trackDestDelete: false,
            trackDestUpdate: false,
            times: [],
            dates: [],
            daysOfWeek: false,
            allSource: false,
          }
        : {
            ...selectedContactSync,
            sourceUser: selectedContactSync.sourceUser
              ? selectedContactSync.sourceUser
              : '',
            targets: selectedContactSync.targets
              ? selectedContactSync.targets
              : [],
            dates: selectedContactSync.dates || [],
            times: selectedContactSync.times || [],
            sources: selectedContactSync.sources || [],
          },
  });

  const scheduled = watch('scheduled');
  const targetGroupName = watch('targetGroupName') || '';
  const syncType = watch('syncType');
  const groupTitle = watch('groupTitle');

  if (syncType === 'bidirectional' && scheduled) {
    steps = [
      'Setup Contact Sync',
      'Sources',
      'Sync Settings',
      'Targets',
      'Schedule',
      'Review',
    ];
  } else if (syncType === 'bidirectional' && !scheduled) {
    steps = [
      'Setup Contact Sync',
      'Sources',
      'Sync Settings',
      'Targets',
      'Review',
    ];
  } else if (syncType !== 'bidirectional' && scheduled) {
    steps = ['Setup Contact Sync', 'Sources', 'Targets', 'Schedule', 'Review'];
  }

  const { mutate: createContactSync, isPending: isCreatingContactSync } =
    useMutation({
      mutationFn: (data: Partial<ContactSyncModel>) =>
        saveContactSync(data, false, changeLogMessage),
      onSuccess: (returnData, data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Added('Contact sync'),
        });

        const newSync = {
          ...data,
          id: returnData,
        } as ContactSyncModel;
        const newList = [...contactSyncList, newSync];
        queryClient.setQueryData([UserKeys.ContactSyncs], newList);
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Adding('this contact sync'),
        });
      },
      onSettled: () => {
        createEvent(analytics.directory.contact_sync.create_contact_sync, {
          module: ModuleList.modules.directory.name,
          section: ModuleList.modules.directory.sections.contactSync,
        });
        setOpenChangeLogDialog(false);
      },
    });

  const { mutate: updateContactSync, isPending: isUpdatingContactSync } =
    useMutation({
      mutationFn: (data: Partial<ContactSyncModel>) =>
        editContactSync(data, changeLogMessage),
      onSuccess: (_returnData, data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Updated('Contact sync'),
        });

        const updatedSync = {
          ...data,
        } as ContactSyncModel;
        const filteredList = contactSyncList.filter(
          (sync) => sync.id !== data.id
        );
        const newList = [...filteredList, updatedSync];
        queryClient.setQueryData([UserKeys.ContactSyncs], newList);
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            Messages.Errors.Network.Updating('this contact sync'),
        });
      },
      onSettled: () => {
        createEvent(analytics.directory.contact_sync.update_contact_sync, {
          module: ModuleList.modules.directory.name,
          section: ModuleList.modules.directory.sections.contactSync,
        });
        setOpenChangeLogDialog(false);
      },
    });

  function containsWeekDays(array1: string[], array2: string[]) {
    for (let i = 0; i < array1.length; i++) {
      if (array2.includes(array1[i])) {
        return true;
      }
    }
    return false;
  }

  const daysOfTheWeek = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ];

  function createTargetGroupName() {
    let name = targetGroupName;
    if (syncType === 'directory' && targetGroupName.length === 0) {
      name = 'Directory Contacts';
    } else if (syncType !== 'directory' && targetGroupName.length === 0) {
      name = groupTitle!;
    }
    return name;
  }

  function handleFormData(data: Partial<ContactSyncModel>) {
    const {
      sources,
      groupTitle,
      groupHref,
      sourceUser,
      runImmediately,
      trackSrcCreate,
      trackSrcDelete,
      trackSrcUpdate,
      trackDestCreate,
      trackDestDelete,
      trackDestUpdate,
      scheduled,
      dates,
      times,
      targets,
    } = data;

    const updatedData =
      dialogType === 'create'
        ? {
            ...data,
            allSourceDirectorySync: false,
            migrated: false,
            lastCompleteTime: 0,
            isCurrentlyRunning: false,
            notifyViaEmail: false,
            sourceUser: syncType !== 'directory' ? sourceUser : '',
            sources: syncType === 'directory' ? sources : [],
            groupTitle: syncType !== 'directory' ? groupTitle : '',
            groupHref: syncType !== 'directory' ? groupHref : '',
            targetGroupName: createTargetGroupName(),
            userModelsCreated: runImmediately ? false : true,
            createdTime: new Date().getTime(),
            lastRunTime: runImmediately ? new Date().getTime() : 0,
            trackSrcCreate:
              syncType === 'bidirectional' ? trackSrcCreate : false,
            trackSrcDelete:
              syncType === 'bidirectional' ? trackSrcDelete : false,
            trackSrcUpdate:
              syncType === 'bidirectional' ? trackSrcUpdate : false,
            trackDestCreate:
              syncType === 'bidirectional' ? trackDestCreate : false,
            trackDestDelete:
              syncType === 'bidirectional' ? trackDestDelete : false,
            trackDestUpdate:
              syncType === 'bidirectional' ? trackDestUpdate : false,
            daysOfWeek: scheduled
              ? containsWeekDays(daysOfTheWeek, dates!)
              : false,
            times: scheduled ? times : [],
            dates: scheduled ? dates : [],
            targets: targets ? targets : [],
          }
        : {
            ...data,
            sources: syncType === 'directory' ? sources : [],
            groupTitle:
              syncType !== 'directory' && groupTitle ? groupTitle : '',
            groupHref: syncType !== 'directory' && groupHref ? groupHref : '',
            sourceUser: syncType !== 'directory' ? sourceUser : '',
            targetGroupName: createTargetGroupName(),
            lastRunTime: runImmediately
              ? new Date().getTime()
              : selectedContactSync.lastRunTime,
            trackSrcCreate:
              syncType === 'bidirectional'
                ? trackSrcCreate
                : selectedContactSync.trackSrcCreate,
            trackSrcDelete:
              syncType === 'bidirectional'
                ? trackSrcDelete
                : selectedContactSync.trackSrcDelete,
            trackSrcUpdate:
              syncType === 'bidirectional'
                ? trackSrcUpdate
                : selectedContactSync.trackSrcUpdate,
            trackDestCreate:
              syncType === 'bidirectional'
                ? trackDestCreate
                : selectedContactSync.trackDestCreate,
            trackDestDelete:
              syncType === 'bidirectional'
                ? trackDestDelete
                : selectedContactSync.trackDestDelete,
            trackDestUpdate:
              syncType === 'bidirectional'
                ? trackDestUpdate
                : selectedContactSync.trackDestUpdate,
            daysOfWeek: scheduled
              ? containsWeekDays(daysOfTheWeek, dates!)
              : false,
            times: scheduled ? times : [],
            dates: scheduled ? dates : [],
          };

    return updatedData;
  }

  const onSubmit: SubmitHandler<Partial<ContactSyncModel>> = (data) => {
    if (
      (isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.AddContactSync.value
      ) &&
        dialogType === 'create') ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditContactSync.value
      ) &&
        dialogType === 'edit')
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (dialogType === 'create') {
        createContactSync(handleFormData(data));
      } else {
        updateContactSync(handleFormData(data));
      }
    }
  };

  function isNameUnique(name: string) {
    const filteredContactSync = contactSyncList.filter(
      (sync) => sync.id !== selectedContactSync?.id
    );

    const labelAlreadyExists = filteredContactSync.some(
      (sync) => sync.targetGroupName === name
    );
    return !labelAlreadyExists;
  }

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <IconButton
        sx={{ position: 'absolute', right: 0, top: 0, m: 1 }}
        onClick={() => {
          handleClose();
        }}
      >
        <Close />
      </IconButton>
      <ContactSyncSteps
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        steps={steps}
        trigger={trigger}
        stepCompleted={stepCompleted}
        setStepCompleted={setStepCompleted}
      />

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 24,
                textAlign: 'center',
              }}
            >
              {steps[activeStep]}
            </Typography>
            <Box
              sx={{
                width: 96,
                height: 2,
                bgcolor: 'primary.main',
                mx: 'auto',
              }}
            ></Box>
          </Box>

          <Box sx={{ height: 500, overflowY: 'auto', width: '100%', px: 1 }}>
            {steps[activeStep] === 'Setup Contact Sync' && (
              <StepOne
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                featureAccess={activeLabs}
                isNameUnique={isNameUnique}
              />
            )}
            {steps[activeStep] === 'Sources' && (
              <StepTwo
                control={control}
                watch={watch}
                domainAutoCompleteResponse={domainAutoCompleteResponse}
                setDomainAutoCompleteResponse={setDomainAutoCompleteResponse}
                domains={domains}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                isNameUnique={isNameUnique}
              />
            )}
            {steps[activeStep] === 'Sync Settings' && (
              <StepThree control={control} />
            )}
            {steps[activeStep] === 'Targets' && (
              <StepFour
                control={control}
                setValue={setValue}
                watch={watch}
                errors={errors}
                domains={domains}
                clearErrors={clearErrors}
                trigger={trigger}
              />
            )}
            {steps[activeStep] === 'Schedule' && (
              <SharedScheduleStep
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                control={control}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                watch={watch}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setValue={setValue}
                errors={errors}
                processName="contact sync"
              />
            )}
            {steps[activeStep] === 'Review' && (
              <StepSix watch={watch} control={control} />
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              if (document.startViewTransition) {
                document.startViewTransition(() => {
                  flushSync(() => {
                    setActiveStep((prevState) => prevState - 1);
                  });
                });
              } else {
                setActiveStep((prevState) => prevState - 1);
              }
            }}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          <Button
            variant="contained"
            loading={
              isValidating ||
              isSubmitting ||
              isCreatingContactSync ||
              isUpdatingContactSync
            }
            type={activeStep === steps.length - 1 ? 'submit' : 'button'}
            onClick={async () => {
              if (activeStep === steps.length - 1) {
                //
              } else {
                const isValid = await trigger();

                const isOnScheduleStep = steps[activeStep] === 'Schedule';
                const timesShouldError =
                  isOnScheduleStep && (getValues('times') || []).length === 0;
                const datesShouldError =
                  isOnScheduleStep && (getValues('dates') || []).length === 0;

                if (timesShouldError) {
                  setError('times', {
                    type: 'manual',
                    message: 'At least one time is required.',
                  });
                }

                if (datesShouldError) {
                  setError('dates', {
                    type: 'manual',
                    message: 'At least one date is required.',
                  });
                }

                if (isValid && !timesShouldError && !datesShouldError) {
                  if (document.startViewTransition) {
                    document.startViewTransition(() => {
                      flushSync(() => {
                        setActiveStep((prevState) => prevState + 1);
                        setStepCompleted((prevState) => {
                          const newState = [...prevState];
                          newState[activeStep] = true;
                          return newState;
                        });
                      });
                    });
                  } else {
                    setActiveStep((prevState) => prevState + 1);
                    setStepCompleted((prevState) => {
                      const newState = [...prevState];
                      newState[activeStep] = true;
                      return newState;
                    });
                  }
                }
              }
            }}
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </DialogActions>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            if (dialogType === 'create') {
              createContactSync(handleFormData(getValues()));
            } else {
              updateContactSync(handleFormData(getValues()));
            }
          }}
          mutateLoading={isCreatingContactSync || isUpdatingContactSync}
        />
      </Box>
    </Dialog>
  );
}
